import { z } from 'zod';
import { BetreuungStatus, FederalState, Nutzungsart } from '../enums.js';
/**
 * Liegenschaft — ein betreutes Objekt an einer Anschrift.
 *
 * Warum es diese Entität gibt: Ein Kunde ist eine Rechnungsadresse, kein Ort.
 * Eine Bäckerei mit einer Zentrale und knapp 40 Filialen ist EIN Kunde mit
 * 41 Objekten — jedes mit eigenen Feuerlöschern, eigenem Begehungsbericht,
 * eigenen Fristen und, weil Filialen über Landesgrenzen gehen können, unter
 * Umständen eigenem Landesrecht. Ohne diese Ebene lässt sich die laufende
 * Betreuung nicht abbilden; der Kunde hätte genau eine Adresse.
 *
 * Abgrenzung zum Projekt: Ein `Project` ist ein Vorhaben mit Anfang und Ende
 * (Nachweis, Konzept, Stellungnahme). Eine Liegenschaft hat kein Ende — sie
 * wird betreut. Ein Projekt kann sich auf eine Liegenschaft beziehen, muss
 * aber nicht (Neubau auf der grünen Wiese).
 */
export const liegenschaft = z.object({
    id: z.number().int().positive(),
    customerId: z.number().int().positive(),
    bezeichnung: z.string(),
    /** Objektnummer des Kunden — er kennt seine Filiale unter SEINER Nummer. */
    externeNummer: z.string().nullable().optional(),
    street: z.string().nullable().optional(),
    houseNumber: z.string().nullable().optional(),
    zip: z.string().nullable().optional(),
    city: z.string().nullable().optional(),
    /**
     * Zusammengesetzte Anschriftzeile — vom Server aus den Einzelfeldern
     * gebildet, nicht vom Client gesetzt. Sie ist zugleich die Grundlage für
     * Geocoding und Volltextsuche.
     */
    address: z.string().nullable().optional(),
    /**
     * Maßgebliches Landesrecht.
     *
     * Wird beim Speichern aus der Anschrift abgeleitet, bleibt aber
     * überschreibbar: Es gibt Gemeinden, durch die eine Landesgrenze läuft, und
     * eingemeindete Ortsteile, die im Nachbarland liegen. Eine automatisch
     * abgeleitete Angabe, die niemand korrigieren kann, wäre hier gefährlich.
     */
    federalState: FederalState.nullable().optional(),
    /** Mehrfachnutzung ist der Normalfall, nicht die Ausnahme. */
    nutzungsarten: z.array(Nutzungsart).default([]),
    status: BetreuungStatus,
    betreuungSeit: z.string().datetime().nullable().optional(),
    betreuungBis: z.string().datetime().nullable().optional(),
    /** Ansprechpartner vor Ort — der Hausmeister, nicht die Buchhaltung. */
    ansprechpartnerId: z.number().int().positive().nullable().optional(),
    grundflaecheQm: z.number().nonnegative().nullable().optional(),
    geschosse: z.number().int().nonnegative().nullable().optional(),
    baujahr: z.number().int().nullable().optional(),
    notizen: z.string().nullable().optional(),
    lat: z.number().nullable().optional(),
    lng: z.number().nullable().optional(),
    geocodedAt: z.string().datetime().nullable().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
const zipSchema = z
    .string()
    .trim()
    .regex(/^\d{4,5}$/, { message: 'PLZ muss 4–5 Ziffern haben' })
    .nullable()
    .optional()
    .or(z.literal(''));
/**
 * Baujahr-Grenzen: nach unten großzügig (Bestandsbauten sind alt), nach oben
 * bewusst offen bis 2100 — ein Objekt kann im Bau sein und ein Fertigstellungs-
 * jahr in der Zukunft tragen.
 */
const baujahrSchema = z.number().int().min(1000).max(2100).nullable().optional();
export const createLiegenschaftRequest = liegenschaft
    .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
    lat: true,
    lng: true,
    geocodedAt: true,
    // Der Server setzt die Anschriftzeile aus den Einzelfeldern zusammen —
    // ein Client, der sie mitschickt, könnte sie von ihnen abweichen lassen.
    address: true,
})
    .extend({
    bezeichnung: z.string().trim().min(2).max(200),
    externeNummer: z.string().trim().max(60).nullable().optional(),
    street: z.string().trim().max(200).nullable().optional(),
    houseNumber: z.string().trim().max(20).nullable().optional(),
    zip: zipSchema,
    city: z.string().trim().max(120).nullable().optional(),
    nutzungsarten: z.array(Nutzungsart).max(14).default([]),
    status: BetreuungStatus.default('aktiv'),
    grundflaecheQm: z.number().nonnegative().max(10_000_000).nullable().optional(),
    geschosse: z.number().int().min(0).max(200).nullable().optional(),
    baujahr: baujahrSchema,
    notizen: z.string().max(5000).nullable().optional(),
});
/** `customerId` ist beim Ändern raus: Ein Objekt wandert nicht zum anderen Kunden. */
export const updateLiegenschaftRequest = createLiegenschaftRequest
    .omit({ customerId: true })
    .partial();
export const LIEGENSCHAFT_SORT_FIELDS = ['bezeichnung', 'city', 'status', 'createdAt'];
export const liegenschaftListQuery = z.object({
    customerId: z.coerce.number().int().positive().optional(),
    search: z
        .string()
        .trim()
        .max(200)
        .optional()
        .transform((v) => (v ? v : undefined)),
    status: BetreuungStatus.optional(),
    nutzungsart: Nutzungsart.optional(),
    federalState: FederalState.optional(),
    /** `feld` = aufsteigend, `-feld` = absteigend (Konvention wie /projects). */
    sort: z
        .string()
        .trim()
        .max(40)
        .optional()
        .transform((v) => (v ? v : undefined)),
    limit: z.coerce.number().int().positive().max(200).default(50),
    offset: z.coerce.number().int().nonnegative().default(0),
});
/**
 * Zählt eine Liegenschaft in diesem Zustand für die laufende Überwachung?
 *
 * Interessenten und beendete Objekte dürfen in Listen und auf der Karte
 * erscheinen, aber keine Prüffristen auslösen — sonst mahnt das System
 * Wartungen an Objekten an, für die niemand beauftragt ist. Ruhend ist die
 * bewusste Pause (Objekt steht leer, Umbau) und zählt ebenfalls nicht.
 */
export function istFristenrelevant(status) {
    return status === 'aktiv';
}
